import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Field } from '../../interfaces/Field';
import { Entity } from '../../interfaces/Entity';
import { capitalize } from '../../utils/stringUtils';
import useGetXdmEntityDataQuery from '../../hooks/queries/useGetXdmEntityDataQuery';
import {
  getXdmEntityNameProperty,
  mapFromXdmEntity,
  getEntityProperty,
} from '../../utils/xdmUtils';
import { Form } from 'semantic-ui-react';
import { XdmEntityObject } from '../../models';

type XdmSelectDropdownProps = {
  fieldInfo: Field;
  value?: Entity | null;
  filter?: ((xdmEntity: XdmEntityObject) => boolean) | null;
  onChange: (newValue: Entity | null) => void;
};

export const XdmSelectDropdown: FC<XdmSelectDropdownProps> = ({
  fieldInfo,
  value,
  onChange,
  filter,
}) => {
  if (!fieldInfo.xdm) {
    throw new Error('XdmSelectDropdown cannot be rendered for non xdm field');
  }

  const { xdm: xdmInfo } = fieldInfo;
  const { entityName } = xdmInfo;
  const { data: xdmData, isLoading: xdmDataLoading } =
    useGetXdmEntityDataQuery(entityName);

  const filteredXdmData = useMemo(() => {
    if (filter) {
      return (xdmData || []).filter(filter);
    }

    return xdmData || [];
  }, [xdmData, filter]);

  const nameProperty = useMemo(() => {
    return getXdmEntityNameProperty(xdmInfo.mappings);
  }, [xdmInfo]);

  const idProperty = useMemo(() => {
    return xdmInfo.key;
  }, [xdmInfo]);

  const entityIdProperty = useMemo(() => {
    return getEntityProperty(xdmInfo.mappings, idProperty);
  }, [idProperty, xdmInfo.mappings]);

  useEffect(() => {
    if (!filteredXdmData || !value) {
      return;
    }

    if (!idProperty) {
      throw new Error('entity key is not defined');
    }

    const selectedValueNotAvailable = !filteredXdmData.find(
      (x) => x[idProperty] === value[entityIdProperty]
    );

    if (selectedValueNotAvailable) {
      onChange(null);
    }
  }, [
    fieldInfo,
    filteredXdmData,
    idProperty,
    entityIdProperty,
    value,
    onChange,
  ]);

  const options = useMemo<{ text: string; value: string }[]>(() => {
    if (xdmDataLoading) {
      return [];
    }

    return (
      filteredXdmData?.map((item) => {
        return {
          text: item[nameProperty],
          value: item[idProperty],
        };
      }) || []
    );
  }, [filteredXdmData, xdmDataLoading, idProperty, nameProperty]);

  const getXdmEntityById = useCallback(
    (id: string) => {
      return filteredXdmData?.find((i) => i[idProperty].toString() === id);
    },
    [filteredXdmData, idProperty]
  );

  const onSelectHandler = useCallback(
    (selectedValue: string) => {
      if (!filteredXdmData) {
        return;
      }

      if (!selectedValue) {
        onChange(null);
        return;
      }

      const selectedXdmEntity = getXdmEntityById(selectedValue.toString());
      if (!selectedXdmEntity) {
        throw new Error(`Xdm Entity not found! id ${selectedValue}`);
      }

      const selectedEntity = mapFromXdmEntity(
        xdmInfo.mappings,
        selectedXdmEntity
      );

      onChange(selectedEntity);
    },
    [filteredXdmData, xdmInfo.mappings, onChange, getXdmEntityById]
  );

  return (
    <Form.Dropdown
      fluid
      label={capitalize(fieldInfo.name)}
      options={options}
      placeholder="choose an option..."
      selection
      clearable
      search
      value={value?.[entityIdProperty]}
      onChange={(event, data) => {
        onSelectHandler(data.value?.toString() ?? '');
      }}
    />
  );
};
